import { InfoOutlined } from '@mui/icons-material'
import { FormControlLabel, IconButton, Skeleton, Stack, Switch, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { AssessmentLinkResolver } from '../assessmentLinks'
import type { ProfileAreaSection } from '../profileLogic'
import ScoreLegend from './ScoreLegend'
import TopicPerformanceTable from './TopicPerformanceTable'

interface OverallRiskToggle {
  checked: boolean
  onChange: (checked: boolean) => void
}

interface TopicPerformanceSectionProps {
  sections: ProfileAreaSection[]
  filterLabel: string
  isLoading: boolean
  overallRisk?: OverallRiskToggle
  emptyState?: React.ReactNode
  screeningTemplateIds?: Set<number>
  resolveLink: AssessmentLinkResolver
}

const DESCRIPTION_MAX_WIDTH = 750

const TopicPerformanceSection: React.FC<TopicPerformanceSectionProps> = ({
  sections,
  filterLabel,
  isLoading,
  overallRisk,
  emptyState,
  screeningTemplateIds,
  resolveLink,
}) => {
  const { formatMessage } = useIntl()

  const content = () => {
    if (isLoading)
      return (
        <Stack spacing={2}>
          {[0, 1, 2].map(key => (
            <Skeleton key={key} variant="rounded" height={120} />
          ))}
        </Stack>
      )
    if (sections.length === 0) return emptyState ?? null

    return (
      <>
        <ScoreLegend
          action={
            overallRisk && (
              <FormControlLabel
                control={
                  <Switch checked={overallRisk.checked} onChange={(_, checked) => overallRisk.onChange(checked)} />
                }
                label={formatMessage({ id: 'sustainabilityProfile.showOverallRisk' })}
                sx={{ mr: 0 }}
              />
            )
          }
        />
        <TopicPerformanceTable
          sections={sections}
          filterLabel={filterLabel}
          showOverallRisk={overallRisk?.checked ?? false}
          screeningTemplateIds={screeningTemplateIds}
          resolveLink={resolveLink}
        />
      </>
    )
  }

  return (
    <Stack spacing={3}>
      <Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
          <Typography variant="subtitle1">{formatMessage({ id: 'sustainabilityProfile.topics.title' })}</Typography>
          <Tooltip
            arrow
            title={
              <Stack spacing={1}>
                <span>{formatMessage({ id: 'sustainabilityProfile.topics.info' })}</span>
                <span>{formatMessage({ id: 'sustainabilityProfile.topics.infoDataSource' })}</span>
              </Stack>
            }
          >
            <IconButton size="small" aria-label={formatMessage({ id: 'sustainabilityProfile.topics.infoAria' })}>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="body1" color="text.secondary" maxWidth={DESCRIPTION_MAX_WIDTH}>
          {formatMessage({ id: 'sustainabilityProfile.topics.description' })}
        </Typography>
      </Stack>
      {content()}
    </Stack>
  )
}

export default TopicPerformanceSection
